export const ACQUISITION_KEYS_BY_PARAMETER_NAME = {
  NOx: ["nox"],
  CO: ["co"],
  CO2: ["co2"],
  O2: ["o2"],
  "SO2 auto": ["so2"],
  CH4: ["ch4"],
  COVT: ["c3H8"],
  COVNM: ["c3H8", "ch4"],
};

export const GAS_LABELS = {
  no: "NO",
  nox: "NOx",
  co: "CO",
  co2: "CO2",
  o2: "O2",
  so2: "SO2",
  c3H8: "COVT",
  ch4: "CH4",
};

export const CORRECTION_GASES = ["o2", "co2"];

export const DAY_MS = 86_400_000;

export const windowBetween = (startDate, endDate, parse = Date.parse) => {
  const start = parse(startDate);
  const end = parse(endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end: end < start ? end + DAY_MS : end };
};

// INFO - G.R - 21/10/2026 - Colonnes 1 à 10 du CSV, dans l'ordre lu par le back (ACQUISITION_INDEX).
const CSV_COLUMNS = [
  "no",
  "nox",
  "co",
  "co2",
  "o2",
  "so2",
  "n2o",
  "c3H8",
  "ch4",
  "temperature",
];
const CSV_DATE = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;

// INFO - G.R - 21/10/2026 - Le client REST renomme la clé c3H8 de graphsPoint en c3h8.
const graphKeyOf = (gas) => (gas === "c3H8" ? "c3h8" : gas);

const seriesOf = (graphsPoint, gas) => graphsPoint?.[graphKeyOf(gas)] ?? [];

const datesOf = (graph) => graph?.concentrations?.x ?? [];

const globalDatesOf = (graphsPoint, gas) =>
  datesOf(seriesOf(graphsPoint, gas).find(({ trial }) => !trial));

const hasTrialData = (graphsPoint, gas) =>
  seriesOf(graphsPoint, gas).some(
    (graph) => graph.trial && datesOf(graph).length
  );

const toSortedTimes = (dates) =>
  dates
    .map((date) => Date.parse(date))
    .filter(Number.isFinite)
    .toSorted((a, b) => a - b);

export const summarizeAcquisition = (
  { graphsPoint, trialsHours },
  { withTimes = false } = {}
) => {
  const summary = {
    start: null,
    end: null,
    gases: [],
    trialGases: [],
    trialCount: Object.keys(trialsHours ?? {}).length,
    times: {},
  };
  for (const gas of Object.keys(GAS_LABELS)) {
    const dates = globalDatesOf(graphsPoint, gas);
    if (hasTrialData(graphsPoint, gas)) summary.trialGases.push(gas);
    if (!dates.length) continue;
    summary.gases.push(gas);
    const [first, last] = [Date.parse(dates[0]), Date.parse(dates.at(-1))];
    summary.start = Math.min(summary.start ?? first, first);
    summary.end = Math.max(summary.end ?? last, last);
    if (withTimes && CORRECTION_GASES.includes(gas))
      summary.times[gas] = toSortedTimes(dates);
  }
  return summary;
};

const lowerBound = (values, target) => {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (values[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low;
};

export const coversWindow = (times, start, end) => {
  if (!times?.length || !Number.isFinite(start) || !Number.isFinite(end))
    return false;
  const first = lowerBound(times, start);
  return first + 1 < times.length && times[first + 1] <= end;
};

export const servesParameterTrials = (summary, parameter) => {
  const gases = ACQUISITION_KEYS_BY_PARAMETER_NAME[parameter?.name];
  if (!summary?.trialCount) return false;
  if (!gases) return true;
  return gases.every((gas) => summary.trialGases.includes(gas));
};

export const correctionGasesOf = (summary) =>
  CORRECTION_GASES.filter((gas) => summary?.gases.includes(gas));

export const gasLabels = (gases) =>
  gases
    .filter((gas) => GAS_LABELS[gas])
    .map((gas) => GAS_LABELS[gas])
    .join(" ");

const parseCsvDate = (cell) => {
  const match = CSV_DATE.exec(cell?.trim() ?? "");
  if (!match) return null;
  const [, day, month, year, hours, minutes, seconds] = match.map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes, seconds);
};

const parseCsvNumber = (cell) => {
  const text = cell?.trim();
  if (!text) return null;
  const value = Number.parseFloat(text.replace(",", "."));
  return Number.isNaN(value) ? null : value;
};

const readCsvRow = (cells, summary) => {
  CSV_COLUMNS.forEach((gas, index) => {
    const value = parseCsvNumber(cells[index + 1]);
    if (value === null) return;
    summary.gases.add(gas);
    if (gas === "o2") summary.minO2 = Math.min(summary.minO2, value);
  });
};

export const readAcquisitionCsv = (text) => {
  const summary = {
    start: Infinity,
    end: -Infinity,
    gases: new Set(),
    minO2: Infinity,
  };
  const lines = text.split("\n");
  for (let index = 1; index < lines.length; index += 1) {
    const cells = lines[index].split(";");
    const time = parseCsvDate(cells[0]);
    if (time === null) continue;
    summary.start = Math.min(summary.start, time);
    summary.end = Math.max(summary.end, time);
    readCsvRow(cells, summary);
  }
  if (!Number.isFinite(summary.start)) return null;
  return {
    start: summary.start,
    end: summary.end,
    gases: [...summary.gases],
    minO2: Number.isFinite(summary.minO2) ? summary.minO2 : null,
  };
};
